/**
 * The REQUEST CRITERIA recap — the criteria a research request was submitted with.
 *
 * E06-F02-T10 built it for the Run screen (re-entering Run for an `in_review` request
 * rendered an empty shell). E07-F04-T16 lifts it out of ResearchRunView so the Review
 * screen shows the SAME recap: without it a long-list mixing several countries left the
 * reviewer unable to tell a geo bug from a geo the request actually asked for.
 *
 * One projection (`criteriaChips`, researchRunModel.ts) feeds both screens, so Run and
 * Review can never describe one request differently. A request with no stored
 * criteria renders the explicit "Criteria are not available for this request." note —
 * never a silent blank.
 *
 * Rendered as plain React children (escaped) — the values are recruiter-entered free text.
 */
import { useLayoutEffect, useRef, useState, type JSX } from 'react';
import type { IntakeCriteria } from '@eer/shared';
import { criteriaChips } from './researchRunModel.js';
import { useResearchStatus } from './researchOrchestratorApi.js';

export const CRITERIA_UNAVAILABLE_NOTE = 'Criteria are not available for this request.';

export interface RequestCriteriaPanelProps {
  readonly criteria: IntakeCriteria | null | undefined;
  /** data-testid prefix: `run` → `run-criteria-panel`, `run-criteria-<key>`, … */
  readonly testIdPrefix?: string;
  /**
   * Dense single-block layout (Review screen): the table below needs the height, so
   * the lines wrap inline instead of a two-column definition grid. Criteria chips stay
   * fully visible; a long brief is clamped to 2 whole lines with a "Show full brief"
   * toggle (E07-F04-T23).
   */
  readonly compact?: boolean;
}

export function RequestCriteriaPanel({
  criteria,
  testIdPrefix = 'run',
  compact = false,
}: RequestCriteriaPanelProps): JSX.Element {
  const chips = criteriaChips(criteria);
  const brief = criteria?.freeText?.trim() ?? '';
  const tid = (suffix: string): string => `${testIdPrefix}-criteria-${suffix}`;
  return (
    <div
      className={`border border-gray-200 rounded-lg bg-slate-50/60 ${compact ? 'mb-4' : 'mb-6'}`}
      data-testid={tid('panel')}
      data-criteria-count={chips.length}
    >
      <div
        className={`px-4 border-b border-gray-100 flex items-center justify-between ${
          compact ? 'py-1.5' : 'py-2'
        }`}
      >
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
          <i className="fa-solid fa-sliders text-[#D4AF37] mr-1" aria-hidden /> Request criteria
        </span>
        <span className="text-[10px] text-gray-400">as submitted</span>
      </div>
      {chips.length > 0 ? (
        <dl
          className={
            compact
              ? 'px-4 py-2 flex flex-wrap gap-x-5 gap-y-1 text-xs'
              : 'px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm'
          }
        >
          {chips.map((chip) => (
            <div
              key={chip.key}
              className={compact ? 'flex gap-1.5 items-baseline' : 'flex gap-2'}
              data-testid={tid(chip.key)}
            >
              <dt
                className={
                  compact
                    ? 'text-[10px] uppercase tracking-wide text-gray-400 whitespace-nowrap'
                    : 'text-[11px] uppercase tracking-wide text-gray-400 min-w-[7.5rem] pt-0.5'
                }
              >
                {chip.label}
              </dt>
              <dd className="text-slate-700 font-medium break-words">{chip.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p
          className={`px-4 text-xs text-gray-400 ${compact ? 'py-2' : 'py-3'}`}
          data-testid={tid('empty')}
        >
          {CRITERIA_UNAVAILABLE_NOTE}
        </p>
      )}
      {brief.length > 0 ? (
        compact ? (
          <CompactBrief brief={brief} tid={tid} />
        ) : (
          <p
            className="px-4 pb-3 text-xs text-slate-600 whitespace-pre-wrap"
            data-testid={tid('brief')}
          >
            <BriefLabel />
            {brief}
          </p>
        )
      ) : null}
    </div>
  );
}

function BriefLabel(): JSX.Element {
  return <span className="text-[11px] uppercase tracking-wide text-gray-400 mr-2">Brief</span>;
}

/**
 * E07-F04-T23 — the Review-screen brief: clamped to 2 WHOLE lines with an explicit
 * "Show full brief" toggle. The padding lives on the wrapper, never on the clamped
 * element: `overflow: hidden` clips at the padding edge, so padding on the clamped
 * `<p>` let the top half of line 3 bleed into view (the "cut mid-line" bug).
 * The toggle appears only when the text actually overflows 2 lines (measured), and
 * stays visible once expanded so the brief can be collapsed again.
 */
function CompactBrief({
  brief,
  tid,
}: {
  brief: string;
  tid: (suffix: string) => string;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return undefined;
    const measure = (): void => setOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [brief, expanded]);

  return (
    <div className="px-4 pb-2">
      <p
        ref={ref}
        className={`text-xs text-slate-600 whitespace-pre-wrap break-words ${
          expanded ? '' : 'line-clamp-2'
        }`}
        data-testid={tid('brief')}
        data-expanded={expanded ? 'true' : 'false'}
      >
        <BriefLabel />
        {brief}
      </p>
      {overflowing || expanded ? (
        <button
          type="button"
          className="mt-0.5 text-[11px] font-semibold text-[#B8962E] hover:underline"
          aria-expanded={expanded}
          data-testid={tid('brief-toggle')}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show full brief'}
        </button>
      ) : null}
    </div>
  );
}

/**
 * E07-F04-T16 — the Review-screen mount. Reads the criteria from the same status
 * snapshot (`research_status.request`) the Run screen and the other Review discovery
 * panels use (react-query dedupes the fetch). While the snapshot is still loading
 * nothing is shown (no flash of the "not available" note); once it has loaded — or
 * failed — a missing criteria object renders the honest note.
 */
export function ReviewRequestCriteria({ requestId }: { requestId: number }): JSX.Element | null {
  const { data, isLoading } = useResearchStatus(requestId);
  if (isLoading && !data) return null;
  return (
    <RequestCriteriaPanel
      criteria={data?.request?.criteria ?? null}
      testIdPrefix="review"
      compact
    />
  );
}
